import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../assets/css/app.css";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import SmartButtonIcon from "@mui/icons-material/SmartButton";
import EditIcon from "@mui/icons-material/Edit";
import { CircularProgress } from "@mui/material";
import Cookies from "js-cookie";
import { Button, Collapse, Input, Popconfirm, Select, Switch, message } from "antd";

const API_BASE = "https://www.smart-lend.com/api";
const { TextArea } = Input;

const ensureCustomBlots = () => {
  if (window.__smartlendNewsBlotsRegistered) {
    return;
  }

  const BlockEmbed = Quill.import("blots/block/embed");
  const BaseImage = Quill.import("formats/image");
  const SizeStyle = Quill.import("attributors/style/size");

  SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px", "42px"];
  Quill.register(SizeStyle, true);

  class SmartLendImageBlot extends BaseImage {
    static create(value) {
      const node = super.create(typeof value === "string" ? value : value?.url);
      if (value && typeof value === "object") {
        if (value.alt) {
          node.setAttribute("alt", value.alt);
        }
        if (value.width) {
          node.setAttribute("data-width", value.width);
          node.setAttribute("width", value.width);
          node.style.width = value.width;
          node.style.maxWidth = "100%";
          node.style.height = "auto";
        }
      }
      return node;
    }

    static formats(node) {
      return {
        alt: node.getAttribute("alt") || "",
        width: node.getAttribute("data-width") || node.style.width || "",
      };
    }

    format(name, value) {
      if (name === "alt") {
        if (value) {
          this.domNode.setAttribute("alt", value);
        } else {
          this.domNode.removeAttribute("alt");
        }
        return;
      }

      if (name === "width") {
        if (value) {
          this.domNode.setAttribute("data-width", value);
          this.domNode.setAttribute("width", value);
          this.domNode.style.width = value;
          this.domNode.style.maxWidth = "100%";
          this.domNode.style.height = "auto";
        } else {
          this.domNode.removeAttribute("data-width");
          this.domNode.removeAttribute("width");
          this.domNode.style.removeProperty("width");
        }
        return;
      }

      super.format(name, value);
    }

    static value(node) {
      return {
        url: node.getAttribute("src"),
        alt: node.getAttribute("alt") || "",
        width: node.getAttribute("data-width") || node.style.width || "",
      };
    }
  }

  SmartLendImageBlot.blotName = "image";
  SmartLendImageBlot.tagName = "IMG";

  class DividerBlot extends BlockEmbed {}
  DividerBlot.blotName = "divider";
  DividerBlot.tagName = "HR";

  class CtaBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      node.setAttribute("href", value?.url || "#");
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
      node.textContent = value?.label || "Learn more";
      node.setAttribute(
        "style",
        "display:inline-block;padding:12px 18px;border-radius:999px;background:#0e5aa5;color:#ffffff;font-weight:800;text-decoration:none;font-family:Manrope, sans-serif;margin:8px 0;",
      );
      return node;
    }

    static value(node) {
      return {
        label: node.textContent || "",
        url: node.getAttribute("href") || "#",
      };
    }
  }
  CtaBlot.blotName = "cta";
  CtaBlot.tagName = "A";

  Quill.register(SmartLendImageBlot, true);
  Quill.register(DividerBlot, true);
  Quill.register(CtaBlot, true);
  window.__smartlendNewsBlotsRegistered = true;
};

ensureCustomBlots();

const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const createEmptyArticle = () => ({
  content_title: "",
  slug: "",
  type_of_content: "News",
  publish_date: "",
  thumbnail: null,
  content: "",
  excerpt: "",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  meta_robots: "index,follow",
  status: true,
});

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  return doc.body.textContent || "";
};

const NewsAndEvents = () => {
  const quillRef = useRef(null);
  const selectedImageRef = useRef(null);
  const [newsAndEvents, setNewsAndEvents] = useState([]);
  const [activeArticle, setActiveArticle] = useState(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [editorUploading, setEditorUploading] = useState(false);

  const updateActiveArticle = (patch) => {
    setActiveArticle((current) => ({ ...current, ...patch }));
  };

  const fetchAllNews = async () => {
    try {
      const response = await fetch(`${API_BASE}/news_and_events/getAll`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
      });
      if (!response.ok) {
        throw new Error("Unable to load articles.");
      }
      const json = await response.json();
      setNewsAndEvents(json.data || []);
    } catch (error) {
      message.error(error.message);
    }
  };

  useEffect(() => {
    fetchAllNews();
  }, []);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const uploadFile = async (file, setLoading) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API_BASE}/file/uploadUniversalFile`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: formData,
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "File upload failed.");
      }

      return json.url;
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const url = await uploadFile(file, setThumbnailUploading);
      updateActiveArticle({ thumbnail: url, og_image: activeArticle?.og_image || url });
    } catch (error) {
      message.error(error.message);
    }
  };

  const insertEditorImage = async () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.click();

    fileInput.onchange = async () => {
      const file = fileInput.files?.[0];
      if (!file) return;

      try {
        const url = await uploadFile(file, setEditorUploading);
        const altText = window.prompt("Alt text for this image:", file.name || "") || "";
        const widthValue = window.prompt(
          "Image width inside article? Example: 100%, 80%, 50%",
          "100%",
        ) || "100%";

        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
        editor.insertEmbed(range.index, "image", {
          url,
          alt: altText,
          width: widthValue,
        });
        editor.setSelection(range.index + 1, 0);
      } catch (error) {
        message.error(error.message);
      }
    };
  };

  const insertDivider = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
    editor.insertEmbed(range.index, "divider", true);
    editor.insertText(range.index + 1, "\n");
    editor.setSelection(range.index + 2, 0);
  };

  const insertCta = () => {
    const label = window.prompt("CTA button label:", "Apply Now");
    if (!label) return;
    const url = window.prompt("CTA button URL:", "https://smart-lend.com");
    if (!url) return;

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
    editor.insertEmbed(range.index, "cta", { label, url });
    editor.insertText(range.index + 1, "\n");
    editor.setSelection(range.index + 2, 0);
  };

  const getSelectedImageBlot = () => {
    if (selectedImageRef.current && window.Quill?.find) {
      const blotFromClick = window.Quill.find(selectedImageRef.current);
      if (blotFromClick?.statics?.blotName === "image") {
        return blotFromClick;
      }
    }

    const editor = quillRef.current?.getEditor();
    if (!editor) return null;

    const range = editor.getSelection(true);
    if (!range) {
      return null;
    }

    const [blot] = editor.getLeaf(range.index);
    if (!blot || blot.statics?.blotName !== "image") {
      return null;
    }

    return blot;
  };

  const editSelectedImage = () => {
    const blot = getSelectedImageBlot();
    if (!blot) {
      message.info("Click an image inside the editor first.");
      return;
    }

    const currentFormats = blot.formats?.() || {};
    const currentAlt = currentFormats.alt || blot.domNode?.getAttribute("alt") || "";
    const currentWidth =
      currentFormats.width ||
      blot.domNode?.getAttribute("data-width") ||
      blot.domNode?.style?.width ||
      "100%";

    const alt = window.prompt("Image alt text:", currentAlt);
    if (alt === null) return;

    const width = window.prompt(
      "Image width percentage. Example: 100%, 80%, 50%",
      currentWidth,
    );

    blot.format("alt", alt.trim());
    if (width !== null && width.trim()) {
      blot.format("width", width.trim());
    }
  };

  const syncInlineImageAttributes = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    editor.root.querySelectorAll("img").forEach((image) => {
      const widthValue = image.style.width || image.getAttribute("width") || "";
      if (widthValue) {
        image.setAttribute("data-width", widthValue);
        image.setAttribute("width", widthValue);
        image.style.maxWidth = "100%";
        image.style.height = "auto";
      }
    });
  };

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return undefined;

    const handleRootClick = (event) => {
      selectedImageRef.current = event.target?.tagName === "IMG" ? event.target : null;
    };

    const handleChange = () => {
      syncInlineImageAttributes();
    };

    editor.root.addEventListener("click", handleRootClick);
    editor.on("text-change", handleChange);
    return () => {
      editor.root.removeEventListener("click", handleRootClick);
      editor.off("text-change", handleChange);
    };
  }, [activeArticle?.id]);

  const saveArticle = async () => {
    if (!activeArticle?.content_title?.trim()) {
      message.error("Content title is required.");
      return;
    }

    try {
      const payload = {
        ...activeArticle,
        slug: activeArticle.slug?.trim() || slugify(activeArticle.content_title),
        excerpt:
          activeArticle.excerpt?.trim() ||
          stripHtml(activeArticle.content).slice(0, 180),
      };

      const response = await fetch(`${API_BASE}/news_and_events/createOrUpdate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to save article.");
      }

      message.success(activeArticle.id ? "Article updated!" : "Article published!");
      setActiveArticle(null);
      await fetchAllNews();
    } catch (error) {
      message.error(error.message);
    }
  };

  const deleteArticle = async (id) => {
    try {
      const response = await fetch(`${API_BASE}/news_and_events/delete/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to delete article.");
      }
      message.success("Article deleted.");
      if (activeArticle?.id === id) {
        setActiveArticle(null);
      }
      setNewsAndEvents((current) => current.filter((item) => item.id !== id));
    } catch (error) {
      message.error(error.message);
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: "#news-editor-toolbar",
        handlers: {
          image: insertEditorImage,
          divider: insertDivider,
          cta: insertCta,
          editImage: editSelectedImage,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [],
  );

  const formats = [
    "header",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
    "background",
    "align",
    "divider",
    "cta",
  ];

  return (
    <div
      style={{
        height: "calc(100% - 80px)",
        width: "calc(100% - 40px)",
        marginLeft: "20px",
        background:
          "transparent linear-gradient(180deg, #FFFFFFB3 0%, #FFFFFF00 100%) 0% 0% no-repeat padding-box",
        padding: "20px",
        borderRadius: "10px",
        marginTop: "5px",
        color: "black",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {activeArticle ? (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "18px",
            }}
          >
            <div
              style={{
                color: "#142C44CC",
                fontSize: "20px",
                fontFamily: "Manrope,sans-serif",
                fontWeight: 800,
                cursor: "pointer",
              }}
              onClick={() => setActiveArticle(null)}
            >
              Edit News / Event
            </div>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {activeArticle?.slug ? (
                <Button
                  type="default"
                  onClick={() =>
                    window.open(
                      `https://smart-lend.com/news-events/${activeArticle.slug}`,
                      "_blank",
                    )
                  }
                >
                  Preview Link
                </Button>
              ) : null}
              <Button type="primary" onClick={saveArticle}>
                {activeArticle.id ? "Update" : "Publish"}
              </Button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: screenWidth > 1200 ? "1fr 1fr 1fr" : "1fr",
              gap: "16px",
              marginBottom: "18px",
            }}
          >
            <Field label="Content Title">
              <Input
                value={activeArticle.content_title || ""}
                onChange={(e) => {
                  const contentTitle = e.target.value;
                  updateActiveArticle({
                    content_title: contentTitle,
                    slug:
                      activeArticle.slug === "" || activeArticle.slug === slugify(activeArticle.content_title)
                        ? slugify(contentTitle)
                        : activeArticle.slug,
                    seo_title: activeArticle.seo_title || contentTitle,
                    og_title: activeArticle.og_title || contentTitle,
                  });
                }}
                placeholder="News title"
              />
            </Field>
            <Field label="URL Slug">
              <Input
                value={activeArticle.slug || ""}
                onChange={(e) =>
                  updateActiveArticle({ slug: slugify(e.target.value) })
                }
                placeholder="article-slug"
              />
            </Field>
            <Field label="Content Type">
              <Select
                value={activeArticle.type_of_content || "News"}
                onChange={(value) => updateActiveArticle({ type_of_content: value })}
                options={[
                  { label: "News", value: "News" },
                  { label: "Events", value: "Events" },
                  { label: "Newsletter", value: "Newsletter" },
                ]}
              />
            </Field>
            <Field label="Shown Publish Date">
              <Input
                type="date"
                value={activeArticle.publish_date || ""}
                onChange={(e) => updateActiveArticle({ publish_date: e.target.value })}
              />
            </Field>
            <Field label="Published Status">
              <div style={{ display: "flex", alignItems: "center", gap: "10px", minHeight: 32 }}>
                <Switch
                  checked={!!activeArticle.status}
                  onChange={(checked) => updateActiveArticle({ status: checked })}
                />
                <span>{activeArticle.status ? "Visible" : "Hidden"}</span>
              </div>
            </Field>
          </div>

          <div style={{ marginBottom: "18px" }}>
            <div style={{ marginBottom: 8, fontWeight: 700 }}>Article Thumbnail</div>
            <input
              id="news-thumbnail-upload"
              type="file"
              style={{ display: "none" }}
              onChange={handleThumbnailUpload}
            />
            <div
              onClick={() => document.getElementById("news-thumbnail-upload")?.click()}
              style={{
                width: screenWidth > 1200 ? "24vw" : "100%",
                maxWidth: "360px",
                height: "220px",
                borderRadius: 12,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: activeArticle.thumbnail ? "none" : "2px dashed lightgrey",
                outline: "1px solid rgba(0,0,0,0.15)",
                cursor: "pointer",
                background: "#fff",
              }}
            >
              {activeArticle.thumbnail ? (
                <img
                  src={activeArticle.thumbnail}
                  alt={activeArticle.content_title || "thumbnail"}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : thumbnailUploading ? (
                <CircularProgress color="primary" size={48} />
              ) : (
                <CropOriginalIcon style={{ color: "lightgrey", fontSize: 96 }} />
              )}
            </div>
          </div>

          <Collapse
            defaultActiveKey={["seo"]}
            items={[
              {
                key: "seo",
                label: "SEO & Social Metadata",
                children: (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: screenWidth > 1200 ? "1fr 1fr" : "1fr",
                      gap: "16px",
                    }}
                  >
                    <Field label="Excerpt / Summary">
                      <TextArea
                        rows={3}
                        value={activeArticle.excerpt || ""}
                        onChange={(e) => updateActiveArticle({ excerpt: e.target.value })}
                        placeholder="Short summary shown in previews and used as fallback meta description."
                      />
                    </Field>
                    <Field label="Meta Robots">
                      <Select
                        value={activeArticle.meta_robots || "index,follow"}
                        onChange={(value) => updateActiveArticle({ meta_robots: value })}
                        options={[
                          { label: "index,follow", value: "index,follow" },
                          { label: "noindex,follow", value: "noindex,follow" },
                          { label: "index,nofollow", value: "index,nofollow" },
                          { label: "noindex,nofollow", value: "noindex,nofollow" },
                        ]}
                      />
                    </Field>
                    <Field label="SEO Title">
                      <Input
                        value={activeArticle.seo_title || ""}
                        onChange={(e) => updateActiveArticle({ seo_title: e.target.value })}
                        placeholder="Custom browser/search title"
                      />
                    </Field>
                    <Field label="SEO Keywords">
                      <Input
                        value={activeArticle.seo_keywords || ""}
                        onChange={(e) => updateActiveArticle({ seo_keywords: e.target.value })}
                        placeholder="financing, sme loan, singapore"
                      />
                    </Field>
                    <Field label="SEO Description">
                      <TextArea
                        rows={3}
                        value={activeArticle.seo_description || ""}
                        onChange={(e) => updateActiveArticle({ seo_description: e.target.value })}
                        placeholder="Meta description for search engines"
                      />
                    </Field>
                    <Field label="Canonical URL">
                      <Input
                        value={activeArticle.canonical_url || ""}
                        onChange={(e) => updateActiveArticle({ canonical_url: e.target.value })}
                        placeholder="https://smart-lend.com/news-events/your-slug"
                      />
                    </Field>
                    <Field label="Open Graph Title">
                      <Input
                        value={activeArticle.og_title || ""}
                        onChange={(e) => updateActiveArticle({ og_title: e.target.value })}
                        placeholder="Title used for social sharing"
                      />
                    </Field>
                    <Field label="Open Graph Image">
                      <Input
                        value={activeArticle.og_image || ""}
                        onChange={(e) => updateActiveArticle({ og_image: e.target.value })}
                        placeholder="Absolute image URL"
                      />
                    </Field>
                    <Field label="Open Graph Description">
                      <TextArea
                        rows={3}
                        value={activeArticle.og_description || ""}
                        onChange={(e) => updateActiveArticle({ og_description: e.target.value })}
                        placeholder="Description used for social sharing"
                      />
                    </Field>
                  </div>
                ),
              },
            ]}
          />

          <div id="news-editor-toolbar" style={{ marginTop: 20, marginBottom: 10 }}>
            <span className="ql-formats">
              <select className="ql-header" defaultValue="">
                <option value="1">H1</option>
                <option value="2">H2</option>
                <option value="">Body</option>
              </select>
              <select className="ql-size" defaultValue="">
                <option value="12px">12px</option>
                <option value="14px">14px</option>
                <option value="16px">16px</option>
                <option value="18px">18px</option>
                <option value="24px">24px</option>
                <option value="32px">32px</option>
                <option value="42px">42px</option>
              </select>
            </span>
            <span className="ql-formats">
              <button className="ql-bold" />
              <button className="ql-italic" />
              <button className="ql-underline" />
              <button className="ql-strike" />
              <button className="ql-blockquote" />
            </span>
            <span className="ql-formats">
              <select className="ql-color" />
              <select className="ql-background" />
              <select className="ql-align" />
            </span>
            <span className="ql-formats">
              <button className="ql-list" value="ordered" />
              <button className="ql-list" value="bullet" />
              <button className="ql-indent" value="-1" />
              <button className="ql-indent" value="+1" />
            </span>
            <span className="ql-formats">
              <button className="ql-link" />
              <button className="ql-image" />
              <button className="ql-divider">
                <HorizontalRuleIcon style={{ fontSize: 18 }} />
              </button>
              <button className="ql-cta">
                <SmartButtonIcon style={{ fontSize: 18 }} />
              </button>
              <button className="ql-editImage">
                <EditIcon style={{ fontSize: 18 }} />
              </button>
            </span>
          </div>

          <div style={{ marginBottom: 12, color: "rgba(20,44,68,0.78)", fontSize: 13 }}>
            Click an image, then use the pencil button to update its alt text or width
            percentage after insertion.
          </div>

          {editorUploading ? (
            <div style={{ marginBottom: 12, color: "#0e5aa5", fontWeight: 700 }}>
              Uploading inline image...
            </div>
          ) : null}

          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={activeArticle.content || ""}
            onChange={(content) => updateActiveArticle({ content })}
            modules={modules}
            formats={formats}
            placeholder="Write the article here. Use the toolbar for CTA buttons, image upload, dividers, color, and font size."
          />
        </div>
      ) : (
        <div style={{ width: "100%", height: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                color: "#142C44CC",
                fontSize: "20px",
                fontFamily: "Manrope,sans-serif",
                fontWeight: 800,
              }}
            >
              News & Events
            </div>
            <Button type="primary" onClick={() => setActiveArticle(createEmptyArticle())}>
              New Post
            </Button>
          </div>

          <div
            style={{
              width: "100%",
              flexWrap: "wrap",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              gap: 14,
            }}
          >
            {newsAndEvents.map((article) => (
              <div
                key={article.id}
                onClick={() => setActiveArticle({ ...createEmptyArticle(), ...article })}
                style={{
                  background: article.status ? "#0e5aa52A" : "rgba(0,0,0,0.08)",
                  borderRadius: 12,
                  padding: 12,
                  cursor: "pointer",
                  width: screenWidth > 1200 ? "19vw" : "100%",
                  minWidth: 280,
                }}
              >
                <div
                  style={{
                    background: "rgba(256,256,256,0.5)",
                    width: "100%",
                    height: 180,
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  {article.thumbnail ? (
                    <img
                      src={article.thumbnail}
                      alt={article.content_title}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : null}
                </div>
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      color: "#142C44CC",
                      fontSize: "15px",
                      fontFamily: "Manrope,sans-serif",
                      fontWeight: 800,
                      marginBottom: 10,
                    }}
                  >
                    {article.content_title}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", marginBottom: 10 }}>
                    /news-events/{article.slug || slugify(article.content_title)}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        background: "rgb(14, 90, 165)",
                        padding: "4px 12px",
                        borderRadius: 999,
                        color: "white",
                        fontFamily: "Manrope,sans-serif",
                        fontWeight: 800,
                        fontSize: 12,
                      }}
                    >
                      {article.type_of_content}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Switch
                        checked={!!article.status}
                        onClick={(checked, event) => {
                          event?.stopPropagation?.();
                        }}
                        onChange={async (checked) => {
                          await saveQuickStatus(article, checked, setNewsAndEvents);
                        }}
                      />
                      <Popconfirm
                        title="Delete article?"
                        onConfirm={(event) => {
                          event?.stopPropagation?.();
                          deleteArticle(article.id);
                        }}
                        onCancel={(event) => event?.stopPropagation?.()}
                      >
                        <Button
                          danger
                          size="small"
                          onClick={(event) => event.stopPropagation()}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={{ marginBottom: 8, fontWeight: 700 }}>{label}</div>
    {children}
  </div>
);

const saveQuickStatus = async (article, checked, setNewsAndEvents) => {
  try {
    const response = await fetch(`${API_BASE}/news_and_events/createOrUpdate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${Cookies.get("token")}`,
      },
      body: JSON.stringify({ ...article, status: checked }),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json?.message || "Unable to update article.");
    }
    setNewsAndEvents((current) =>
      current.map((item) => (item.id === article.id ? json.data : item)),
    );
    message.success("Article status updated.");
  } catch (error) {
    message.error(error.message);
  }
};

export default NewsAndEvents;
